
import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useSecureAuth } from './useSecureAuth';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  userRole: string | null;
  isAdmin: boolean;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  updatePassword: (password: string) => Promise<{ error: any }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { secureSignIn, secureSignUp, secureResetPassword } = useSecureAuth();

  const fetchUserRole = async (userId: string) => {
    try {
      // First get the user role
      const { data: roleData, error: roleError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .maybeSingle();
      
      if (roleError) {
        console.error('Error fetching user role:', roleError);
        return null;
      }

      const userRole = roleData?.role;

      // Skip approval check for admin users
      if (userRole === 'admin') {
        return userRole;
      }

      // Check approval status for non-admin users
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('approval_status')
        .eq('id', userId)
        .maybeSingle();

      if (profileError) {
        console.error('Error fetching user profile:', profileError);
      } else if (profile && profile.approval_status === 'rejected') {
        toast({
          title: "Մուտքը մերժված է",
          description: "Ձեր հաշիվը մերժվել է: Դիմեք ադմինիստրատորին",
          variant: "destructive"
        });
        await supabase.auth.signOut();
        return null;
      } else if (profile && profile.approval_status === 'pending') {
        toast({
          title: "Սպասող հաստատում",
          description: "Ձեր հաշիվը դեռ չի հաստատվել: Դիմեք ադմինիստրատորին",
          variant: "default"
        });
        await supabase.auth.signOut();
        return null;
      }
      
      return userRole || null;
    } catch (error) {
      console.error('Unexpected error fetching user role:', error);
      return null;
    }
  };

  useEffect(() => {
    let roleTimeout: NodeJS.Timeout;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // Only synchronous state updates here to prevent deadlock
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          if (roleTimeout) clearTimeout(roleTimeout);
          
          // Defer async operations using setTimeout to prevent deadlock
          setTimeout(() => {
            const fetchRoleWithRetry = async (attempts = 0) => {
              const role = await fetchUserRole(session.user.id);
              
              if (role) {
                setUserRole(role);
                setLoading(false);
              } else if (attempts < 3) {
                roleTimeout = setTimeout(() => fetchRoleWithRetry(attempts + 1), 1000);
              } else {
                setUserRole(null);
                setLoading(false);
              }
            };

            fetchRoleWithRetry();
          }, 0);
        } else {
          setUserRole(null);
          setLoading(false);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        fetchUserRole(session.user.id).then(role => {
          setUserRole(role);
          setLoading(false);
        });
      } else {
        setLoading(false);
      }
    });

    return () => {
      subscription.unsubscribe();
      if (roleTimeout) clearTimeout(roleTimeout);
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    return await secureSignIn(email, password);
  };

  const signUp = async (email: string, password: string) => {
    return await secureSignUp(email, password);
  };

  const signOut = async () => {
    try {
      // Clear local state immediately
      setUser(null);
      setSession(null);
      setUserRole(null);
      setLoading(false);
      
      // Clear all auth-related localStorage
      localStorage.removeItem('adminAuth');
      localStorage.removeItem('supabase.auth.token');
      sessionStorage.clear();
      
      // Force clear Supabase client session
      await supabase.auth.signOut({ scope: 'local' });
      
      // Additional cleanup - remove any cached data
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          await registration.update();
        }
      }
      
      // Force reload to clear any cached state
      window.location.replace('/');
    } catch (error) {
      console.error('Sign out error:', error);
      // Force clear everything regardless of error
      setUser(null);
      setSession(null);
      setUserRole(null);
      setLoading(false);
      localStorage.clear();
      sessionStorage.clear();
      
      // Force reload to clear any cached state
      window.location.replace('/');
    }
  };

  const resetPassword = async (email: string) => {
    return await secureResetPassword(email);
  };

  const updatePassword = async (password: string) => {
    // Validate password strength using shared policy (allows any special char)
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[^A-Za-z0-9]/.test(password);
    const hasMinLength = password.length >= 8;

    if (!hasMinLength || !hasUppercase || !hasLowercase || !hasNumber || !hasSpecialChar) {
      return { 
        error: { 
          message: 'weak_password',
          details: 'Գաdelays\u0576\u0561\u0562\u0561\u057C\u0568 \u057A\u0565\u057F\u0584 \u0567 \u057A\u0561\u0580\u0578\u0582\u0576\u0561\u056F\u056B \u0574\u0565\u056E\u0561\u057F\u0561\u057C, \u0583\u0578\u0584\u0580\u0561\u057F\u0561\u057C, \u0569\u056B\u057E \u0587 \u0570\u0561\u057F\u0578\u0582\u056F \u0576\u056B\u0577'
        } 
      };
    }

    const { error } = await supabase.auth.updateUser({
      password: password
    });
    return { error };
  };

  const isAdmin = userRole === 'admin';

  return (
    <AuthContext.Provider value={{
      user,
      session,
      userRole,
      isAdmin,
      loading,
      signIn,
      signUp,
      signOut,
      resetPassword,
      updatePassword
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
